import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Button, Card } from './ui';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

const CHUNK_RELOAD_KEY = 'chunkErrorReload';

function isChunkLoadError(error: Error): boolean {
  return (
    error.name === 'ChunkLoadError' ||
    error.message.includes('Loading chunk') ||
    error.message.includes('Loading CSS chunk') ||
    error.message.includes('Failed to fetch dynamically imported module')
  );
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('[ErrorBoundary] Uncaught error:', error);
    console.error('[ErrorBoundary] Component stack:', errorInfo.componentStack);

    if (isChunkLoadError(error)) {
      const lastReload = sessionStorage.getItem(CHUNK_RELOAD_KEY);
      const now = Date.now();
      if (!lastReload || now - parseInt(lastReload, 10) > 30000) {
        console.log('[ErrorBoundary] ChunkLoadError detected, reloading to clear stale cache...');
        sessionStorage.setItem(CHUNK_RELOAD_KEY, now.toString());
        window.location.reload();
        return;
      }
      console.warn('[ErrorBoundary] ChunkLoadError persists after reload -- showing fallback');
    }

    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  handleReset = (): void => {
    this.setState({ hasError: false, error: null });
  };

  handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="flex items-center justify-center min-h-[300px] p-4 md:p-6">
          <Card padding="none" className="flex flex-col items-center max-w-[480px] p-8 md:p-6 text-center shadow-sm">
            <div className="flex items-center justify-center w-12 h-12 mb-4 text-2xl font-bold text-danger bg-danger/10 rounded-full">
              !
            </div>
            <h2 className="m-0 mb-2 text-lg font-semibold text-text-primary leading-snug">
              Something went wrong
            </h2>
            <p className="m-0 mb-5 text-sm text-text-secondary leading-relaxed">
              An unexpected error occurred. You can try again or reload the page.
            </p>
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <pre className="w-full m-0 mb-5 p-3 font-mono text-xs text-danger bg-bg-secondary border border-border-light rounded-lg text-left overflow-x-auto whitespace-pre-wrap break-words">
                {this.state.error.message}
              </pre>
            )}
            <div className="flex gap-3 flex-col md:flex-row w-full md:w-auto">
              <Button
                variant="primary"
                className="w-full md:w-auto"
                onClick={this.handleReset}
              >
                Try Again
              </Button>
              <Button
                variant="secondary"
                className="w-full md:w-auto"
                onClick={this.handleReload}
              >
                Reload Page
              </Button>
            </div>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
